import { useState, useEffect } from "react";
import TeamCard from "./TeamCard";
import type { AdminTeam as Team, AdminGame as Game, AdminPlayer as Player } from "../types/admin";
import { API_BASE_URL } from "../utils/api";

interface ViewTeamsProps {
  game: Game;
  onClose?: () => void;
  refreshKey?: number;
  playerRefreshKey?: number;
  teamsData?: Team[];
  playersByTeam?: Record<string, Player[]>;
  playerActionCounts?: Record<string, number>;
  playerCharCounts?: Record<string, number>;
  playerLandingSiteInvestments?: Record<string, { total_points: number; investments: Array<{player_id: string; player_name: string; team_name: string; count: number}> }>;
}

export default function ViewTeams({
  game,
  onClose,
  refreshKey,
  playerRefreshKey,
  teamsData,
  playersByTeam: playersByTeamProp,
  playerActionCounts,
  playerCharCounts,
  playerLandingSiteInvestments
}: ViewTeamsProps) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [playersByTeam, setPlayersByTeam] = useState<Record<string, Player[]>>({});

  // Use teamsData prop if provided, otherwise fetch as fallback (should rarely happen)
  useEffect(() => {
    // teamsData should always be provided from GameCard/AdminActions
    // Only fetch if explicitly undefined (fallback for edge cases)
    if (teamsData !== undefined) {
      setTeams(Array.isArray(teamsData) ? teamsData : []);
      setIsLoading(false);
      setError("");
    } else {
      // Fallback: fetch teams only if prop is explicitly undefined
      // This should rarely happen as AdminActions always provides teams
      let cancelled = false;
      setIsLoading(true);
      setError("");
      fetch(`${API_BASE_URL}/teams/by-game-name?game_name=${encodeURIComponent(game.game_name)}`)
        .then(res => {
          if (!res.ok) {
            // If 404, it might mean no teams exist yet, which is okay
            if (res.status === 404) {
              return [];
            }
            throw new Error("Failed to fetch teams");
          }
          return res.json();
        })
        .then((data: Team[]) => {
          if (cancelled) return;
          setTeams(Array.isArray(data) ? data : []);
          setIsLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(err.message || "Failed to load teams");
          setIsLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }
  }, [teamsData, game.game_name, game.game_id, refreshKey]);

  // Use playersByTeamProp if provided, otherwise fetch as fallback (should rarely happen)
  useEffect(() => {
    // playersByTeamProp should always be provided from GameCard/AdminActions
    // Only fetch if explicitly undefined (fallback for edge cases)
    if (playersByTeamProp !== undefined) {
      setPlayersByTeam(playersByTeamProp || {});
      setIsLoading(false);
    } else {
      // Fallback: fetch players only if prop is explicitly undefined
      // This should rarely happen as AdminActions always provides playersByTeam
      let cancelled = false;
      setIsLoading(true);
      fetch(`${API_BASE_URL}/players/by-game-name?game_name=${encodeURIComponent(game.game_name)}`)
        .then(res => {
          if (!res.ok) {
            if (res.status === 404) {
              return [];
            }
            throw new Error("Failed to fetch players");
          }
          return res.json();
        })
        .then((data: Player[]) => {
          if (cancelled) return;
          // Group players by team_id (single fetch, grouped locally - no per-team fetches)
          const grouped: Record<string, Player[]> = {};
          data.forEach((player) => {
            if (!grouped[player.team_id]) {
              grouped[player.team_id] = [];
            }
            grouped[player.team_id].push(player);
          });
          setPlayersByTeam(grouped);
          setIsLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("Failed to fetch players:", err);
          setPlayersByTeam({});
          setIsLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }
  }, [playersByTeamProp, game.game_name, refreshKey, playerRefreshKey]);

  return (
    <div style={{
      marginTop: "1rem",
      padding: "1rem",
      backgroundColor: "white",
      borderRadius: "8px",
      border: "1px solid #dee2e6"
    }}>
        

        {isLoading && (
          <p style={{ color: "#6c757d", textAlign: "center", padding: "2rem" }}>
            Loading teams...
          </p>
        )}

        {error && (
          <p style={{ color: "red", padding: "1rem" }}>
            {error}
          </p>
        )}

        {!isLoading && !error && teams.length === 0 && (
          <p style={{ color: "#6c757d", textAlign: "center", padding: "2rem" }}>
            No teams found for this game.
          </p>
        )}

        {!isLoading && !error && teams.length > 0 && (
          <div>
            <div>
              {teams.map((team) => (
                      <TeamCard
                        key={`${team.team_id}-${playerRefreshKey ?? 0}`}
                        team={team}
                        gameStatus={game.game_status}
                        refreshKey={playerRefreshKey}
                        playersData={playersByTeam?.[team.team_id]}
                        currentTurn={game.current_turn}
                        playerActionCounts={playerActionCounts}
                        playerCharCounts={playerCharCounts}
                        playerLandingSiteInvestments={playerLandingSiteInvestments}
                      />
              ))}
            </div>
          </div>
        )}
    </div>
  );
}

